import {
  Annex,
  Appointment,
  DocumentsSigningMode,
  JobGenerationOpts,
  Pagination,
  PersonalDetails,
  PreSurvey,
  Process,
  Purchase,
  User,
  UserDocument,
  UserProcessMode,
  VisitAssistant,
  VoivodeshipOffice,
  UserProcessStatusType,
} from '@interfaces';
import { Moment } from 'moment';

export interface UserProcess {
  id: string;
  isMainStayProcessOfUser: boolean;
  name: string | null;
  status: UserProcessStatusType;
  mode: UserProcessMode;
  purposeComment: string | null;
  hasFullHandling: boolean;
  hasAssistantService: boolean;

  verifiedAt?: Date | null;
  verifiedById?: string | null;
  verifiedBy?: Partial<User> | null;

  confirmedMOSDraftAt?: Date | null;
  confirmedMOSDraftById?: string | null;
  confirmedMOSDraftBy?: Partial<User> | null;

  confirmedSubmissionToMOSAt?: Date | null;
  confirmedSubmissionToMOSById?: string | null;
  confirmedSubmissionToMOSBy?: Partial<User> | null;

  confirmedSubmissionByPOSTAt?: Date | null;
  confirmedSubmissionByPOSTById?: string | null;
  confirmedSubmissionByPOSTBy?: Partial<User> | null;

  decisionProvidedAt: Date | null;
  decisionPermitIssuedAt: Date | null;
  decisionPermitValidUntil: Date | null;
  decisionPermitValidIndefinitely: boolean | null;
  decisionProvidedById: string | null;
  decisionProvidedBy?: User | null;

  userId: string;
  user: User;

  personalDetailsId: string;
  personalDetails: PersonalDetails;
  preSurveyId: string | null;
  preSurvey: PreSurvey | null;
  annex: Annex[];

  processId: string | null;
  process: Process | null;
  purchaseId?: string | null;
  purchase?: Purchase | null;
  // UPSELL purchases of this user-process (only id, status, createdAt)
  purchasedUpsells?: Partial<Purchase>[];

  documentsSigningMode: DocumentsSigningMode | null;

  /**
   * DEPRECATED in favor of Appointment
   * companyOfficePlannedVisitDate: Date | null;
   */

  appointment?: Appointment | null;

  availabilityForOfficeVisit?: AvailabilityForOfficeVisit | null;
  immigrationOfficeVisitDate: Date | null;
  officeVisitReservationNumber: string | null;
  visitAssistant: VisitAssistant;
  confirmedReadinessForVisitAt: Date | null;

  documentsReviewComment: string | null;

  selectedVoivodeshipOfficeId: string | null;
  selectedVoivodeshipOffice?: VoivodeshipOffice | null;

  updatedByUserId: string; // userID
  updatedByUser?: Partial<User>; // userID

  userDocuments?: UserDocument[];
  usingCustomInstructionItemsList: boolean;
  showFeesOnInstructionItemsList: boolean;

  createdAt: Date | string;
  updatedAt: Date | string;
  deletedAt: Date | string;
}

export interface AddNewUserProcessOpts {
  userId: string;
  processId?: string;
  name?: string;
  mode?: UserProcessMode;
  purposeComment?: string;
  hasAssistantService: boolean;
  hasFullHandling: boolean;
}

export interface EditUserProcess {
  userId: string;
  name?: string;
  purposeComment?: string;
  mode?: UserProcessMode;
  hasAssistantService?: boolean;
  hasFullHandling?: boolean;
  selectedVoivodeshipOfficeId?: string;
}

export interface SearchProcessesOfUser extends Pagination {
  userId: string;
  status?: UserProcessStatusType;
  name?: string;
  showDeleted?: boolean;
}

export interface ChangeUserProcessTypeOpts {
  userId: string;
  userProcessId: string;
  newUserProcessId: string;
}

export interface ChangeUserProcessStatusOpts {
  userId: string;
  userProcessId: string;
  newUserProcessStatus: UserProcessStatusType;
}

export enum UserProcessChangeType {
  'USER_PROCESS_CREATED' = 'USER_PROCESS_CREATED', // Not used
  'USER_PROCESS_CHANGED' = 'USER_PROCESS_CHANGED', // Not used
  'USER_PROCESS_TYPE_CHANGED' = 'USER_PROCESS_TYPE_CHANGED',
  'USER_PROCESS_STATUS_CHANGED' = 'USER_PROCESS_STATUS_CHANGED',
  'USER_UPLOADED_DOCUMENT' = 'USER_UPLOADED_DOCUMENT', // Not used
  'USER_REMOVED_UPLOADED_DOCUMENT' = 'USER_REMOVED_UPLOADED_DOCUMENT', // Not used

  'FOREIGNER_SET_APPOINTMENT_DATE' = 'FOREIGNER_SET_APPOINTMENT_DATE',
  'FOREIGNER_UPDATED_APPOINTMENT_DATE' = 'FOREIGNER_UPDATED_APPOINTMENT_DATE',
  'VISIT_DATE_SCHEDULED' = 'VISIT_DATE_SCHEDULED',
  'VISIT_DATA_UPDATED' = 'VISIT_DATA_UPDATED',
  'VISIT_DATE_REMOVED' = 'VISIT_DATE_REMOVED',
  'VISIT_ASSISTANT_CHANGED' = 'VISIT_ASSISTANT_CHANGED',
  'REQUIRED_DOCUMENTS_LIST_CHANGED' = 'REQUIRED_DOCUMENTS_LIST_CHANGED',

  'CHANGED_INCLUDED_SERVICES_IN_PROCESS' = 'CHANGED_INCLUDED_SERVICES_IN_PROCESS',
  'SENDING_TO_MOS_SUCCESS' = 'SENDING_TO_MOS_SUCCESS',
  'SENDING_TO_MOS_ERROR' = 'SENDING_TO_MOS_ERROR',

  'FILES_PERMANENT_DELETED' = 'FILES_PERMANENT_DELETED',

  // Missing (17.03.2025)
  'REMOVE_USER_PROCESS' = 'REMOVE_USER_PROCESS',
  'CREATED_NEW_USER_PROCESS' = 'CREATED_NEW_USER_PROCESS',
}

export interface UserProcessChange {
  id: string;
  type: UserProcessChangeType;
  createdAt: Date;
  changeAuthor: User;
  changeAuthorId: string;
  userProcessId: string;
  userProcess?: UserProcess;
  details?: {
    opts?: JobGenerationOpts;
    visitDate?: Date;
    reason?: string;
    newStatus?: UserProcessStatusType;
    oldStatus?: UserProcessStatusType;
    finishedAt?: Date;
    recordingPath?: string;
    exportDate?: string;
    MOSDocumentId?: string;
    userAssetsFileName?: string;
    userAssetsFileFullPath?: string;
    pdfFilePath?: string;
    error?: {
      name?: string;
      screenshotPath?: string;
      recordingPath?: string;
    };
  };
}

export interface SetVisitDateOpts {
  userId: string;
  userProcessId: string;
  immigrationOfficeVisitDate: string;
  selectedVoivodeshipOfficeId: string;
  assistantId: string | null;
  officeVisitReservationNumber: string | null;
  notifyForeigner: boolean;
  updateStatus: boolean;
}

export interface DeleteImmigrationOfficeVisitDate {
  userId: string;
  userProcessId: string;
  visitDate: string;
}

export interface CreateMyProcesOpts {
  // mode: UserProcessMode;
  processId: string;
  comment?: string;
}

export enum AvailabilityMode {
  'DATE_RANGE' = 'DATE_RANGE',
  'PARTICULAR_DATES' = 'PARTICULAR_DATES',
  'ASAP' = 'ASAP',
}

export interface AvailabilityForOfficeVisit {
  mode: AvailabilityMode;
  // array of dates strings
  dates: string[];
}

export interface VoivoVisitAvailabilityOpts {
  mode: AvailabilityMode;
  startDate?: Moment;
  endDate?: Moment;
  particularDates?: Moment[];
}

export interface SaveDocumentsSigningModeOpts {
  // Will exist only in case of PERSONAL_IN_OFFICE mode and user is updating/changing the date of planned visit
  appointmentId?: string;
  // Will exist only in case of PERSONAL_IN_OFFICE mode and user is updating/changing the date of planned visit
  date?: string;
  // Will exist only in case of PERSONAL_IN_OFFICE mode and user is updating/changing the date of planned visit
  expectedCurrentDate?: string;

  mode: DocumentsSigningMode;
}

export interface UserProcessVisit extends UserProcess {
  eventDate: moment.Moment;
  eventDateAsString: string;
  eventTimeAsString: string;
  eventPlace: string;
  eventForeignerName: string;
  eventAssistantName: string;
  eventType:
    | 'COMPANY_OFFICE_VISIT'
    | 'COMPANY_ONLINE_CONSULTATION'
    | 'VOIVO_OFFICE_VISIT'
    | 'VOIVO_OFFICE_VISIT_WITH_ASSISTANT';
  eventImportant?: boolean;
}

export interface VisitInView {
  [key: string]: { [key: string]: UserProcessVisit[] };
}

export interface goToDateVisits {
  date: string;
  type: string;
}

export interface AssignProcessTypeOpts {
  processId: string;
  purposeComment: string | null;
  userProcessId: string;
  foreignerId: string;
  mode: UserProcessMode;
}

export interface UpdateUserProcessServicesOpts {
  userId: string;
  userProcessId: string;
  payload: {
    hasFullHandling: boolean;
    hasAssistantService: boolean;
  };
}

export interface SetMainStayProcessOfUserOpts {
  userId: string;
  userProcessId: string;
  isMainStayProcessOfUser: boolean;
}

export interface SetAppointmentDateAndPlaceOpts {
  userProcessId: string;
  voivodeshipOfficeId: string;
  immigrationOfficeVisitDate: string;
}

export interface UploadDecisionOpts {
  userProcessId: string;
  decisionPermitIssuedAt: string;
  decisionPermitValidIndefinitely: boolean;
  decisionPermitValidUntil?: string;
}

export interface ManagementUploadDecisionOpts {
  userId: string;
  userProcessId: string;
  decisionPermitIssuedAt: string;
  decisionPermitValidIndefinitely: boolean;
  decisionPermitValidUntil?: string;
}

export enum ProcessFlowAction {
  APPLICATION_VERIFIED = 'APPLICATION_VERIFIED',
  APPOINTMENT_COMPLETED = 'APPOINTMENT_COMPLETED',
  SIGNED_DOCUMENTS_RECEIVED = 'SIGNED_DOCUMENTS_RECEIVED',
  MOS_DRAFT_PREPARED_CONFIRMATION = 'MOS_DRAFT_PREPARED_CONFIRMATION',
  DOCUMENTS_SENT_TO_IMMIGROF = 'DOCUMENTS_SENT_TO_IMMIGROF',
  IMMIGR_OFFICE_VISIT_COMPLETED = 'IMMIGR_OFFICE_VISIT_COMPLETED',
  DECISION_ADDED = 'DECISION_ADDED',
}

export interface ExecuteProcessFlowActionOpts {
  userId: string;
  userProcessId: string;
  actionName: ProcessFlowAction;
}

export interface SetDocumentsSigningModeOpts {
  userId: string;
  userProcessId: string;
  mode: DocumentsSigningMode;
}
